import { useParams } from 'react-router-dom';

import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
  Tooltip,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';

import ValidationMessage from 'components/validation/ValidationMessage';
import DateField from 'components/fields/DateField';
import TimeField from 'components/fields/TimeField';

import SharedTextFieldInput from 'components/fields/shared/TextFieldInput';
import ValidateFile from 'components/fields/shared/Message/components/ValidateFile';
import ValidateText from 'components/fields/shared/Message/components/ValidateText';

import useFormBinderDisabled from 'hooks/useFormBinderDisabled';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

import { FILE_TYPE } from 'variables/constants';

import { useCarriers } from 'hooks/use-carriers-hook';

import { useGapInsuranceResolver, LAPSE_REASON_LABELS } from './hooks';
import { useMarketApprovalResolver } from './hooks';
import {
  getHTMLId,
  useResolverModal,
  parseDateString,
  BINDING_AUTHORITY_MESSAGE,
  MARKET_MISMATCH_MESSAGE,
} from './resolverUtils';
import type { ValidationResolverProps, LapseReasonOption } from './types';

export interface Validation090bResolverProps extends ValidationResolverProps {}

const VALIDATION_ID = '090b';
const FORM_STATE_KEY = 'autoPersonalForm';

const REASON_OPTIONS: LapseReasonOption[] = ['a', 'b', 'c', 'd'];

const Validation090bResolver = ({
  section,
  itemKey: propItemKey,
  compact = false,
}: Validation090bResolverProps) => {
  const { uuid } = useParams();
  const isBinderDisabled = useFormBinderDisabled(FORM_STATE_KEY);
  const isMarketApprovalEmailEmlMsgEnabled = useFeatureFlag(
    'enable-market-approval-email-eml-msg',
  );
  const {
    isOpen: isCompactOpen,
    onOpen: onCompactOpen,
    onClose: onCompactClose,
  } = useDisclosure();

  const itemKey = propItemKey || 'global';
  const marketApprovalItemKey = `${itemKey}-market-approval`;

  const gapState = useGapInsuranceResolver({
    validationId: VALIDATION_ID,
    section,
    itemKey,
    formStateKey: FORM_STATE_KEY,
    fileType: FILE_TYPE.TYPE_090b.type,
    fileLabel: FILE_TYPE.TYPE_090b.text,
  });

  const marketApprovalState = useMarketApprovalResolver({
    validationId: VALIDATION_ID,
    section,
    itemKey: marketApprovalItemKey,
    formStateKey: FORM_STATE_KEY,
    showLOEOption: false,
    marketApprovalFileType: FILE_TYPE.TYPE_MARKET_APPROVAL_AUTO.type,
    marketRequired: true,
  });

  const { carrierOptions } = useCarriers();

  const htmlId = getHTMLId(VALIDATION_ID, itemKey);
  const fieldIds = {
    ...gapState.fieldIds,
    ...marketApprovalState.fieldIds,
  };

  const { handleModalClose } = useResolverModal(
    compact,
    Object.values(fieldIds),
    htmlId,
    onCompactOpen,
    onCompactClose,
  );

  const show = gapState.state.show;
  const alertMessage = gapState.alertMessage;

  const isMarketApprovalComplete =
    marketApprovalState.state.approvalMethod === 'email'
      ? marketApprovalState.state.file &&
        (marketApprovalState.state.file.status === 'approved' ||
          marketApprovalState.state.file.is_deferred)
      : marketApprovalState.state.approvalMethod === 'phone_call' &&
        marketApprovalState.state.approvalDate &&
        marketApprovalState.state.approvalTime &&
        marketApprovalState.state.underwriterName?.trim() &&
        marketApprovalState.state.market?.trim();

  const getCompactAlertStatus = () => {
    if (gapState.getCompactAlertStatus() !== 'success') return 'warning';
    if (!isMarketApprovalComplete) return 'warning';
    return 'success';
  };

  if (!show) return null;

  if (compact) {
    return (
      <>
        <div id={htmlId}></div>
        <div id={fieldIds.reasonOption}></div>
        <div id={fieldIds.otherExplanation}></div>
        <div id={fieldIds.noClaimsConfirmed}></div>
        <div id={fieldIds.approvalMethod}></div>
        <div id={fieldIds.remarks}></div>
        <div id={fieldIds.date}></div>
        <div id={fieldIds.time}></div>
        <div id={fieldIds.underwriter}></div>
        <div id={fieldIds.market}></div>
        <Tooltip placement="top" label={alertMessage}>
          <Alert status={getCompactAlertStatus()} mt="10px" fontSize="14px">
            <Button
              onClick={() => onCompactOpen()}
              w="100%"
              whiteSpace="normal"
            >
              <AlertIcon />
              Add remark
            </Button>
          </Alert>
        </Tooltip>
        <Modal
          isOpen={isCompactOpen}
          onClose={handleModalClose}
          size="4xl"
          scrollBehavior="inside"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              Gap in Insurance (090b) – Market Approval Required
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <Box mb="10px">
                <Validation090bResolver
                  section={section}
                  itemKey={propItemKey}
                  compact={false}
                />
              </Box>
              <Button
                variant="brand"
                onClick={handleModalClose}
                mb="15px"
                float="right"
              >
                Close
              </Button>
            </ModalBody>
          </ModalContent>
        </Modal>
      </>
    );
  }

  return (
    <Box>
      <div id={htmlId}></div>
      <div id={fieldIds.reasonOption}></div>
      <div id={fieldIds.otherExplanation}></div>
      <div id={fieldIds.noClaimsConfirmed}></div>

      <Alert status="warning" mt="10px" fontSize="14px">
        <AlertIcon />
        <ValidationMessage message={alertMessage} />
      </Alert>

      <Box mt="15px" p="15px" bg="gray.50" borderRadius="md">
        {/* Gap in insurance – reason for lapse (same as 090a) */}
        <Text fontWeight="medium" mb="8px">
          Was the client cancelled by the insurer prior to the lapse for any of
          the following reasons?
        </Text>
        <VStack
          align="stretch"
          spacing="8px"
          mb="12px"
          id={fieldIds.reasonOption}
        >
          {REASON_OPTIONS.map((option) => (
            <Checkbox
              key={option}
              isChecked={gapState.state.reasonOption === option}
              onChange={gapState.handlers.handleReasonOptionChange(option)}
              isDisabled={isBinderDisabled}
            >
              {option}. {LAPSE_REASON_LABELS[option]}
            </Checkbox>
          ))}
        </VStack>

        {gapState.state.reasonOption === 'd' && (
          <Box mb="12px" id={fieldIds.otherExplanation}>
            <SharedTextFieldInput
              label="Other reason for lapse"
              value={gapState.state.otherText}
              onChange={gapState.handlers.handleOtherTextChange}
              borderColor={
                !gapState.state.otherText ||
                gapState.state.otherText.trim().length === 0
                  ? 'red'
                  : 'green'
              }
              formStateKey={FORM_STATE_KEY}
              section={section}
            />
          </Box>
        )}

        {gapState.state.remark &&
          (gapState.state.reasonOption === 'd' ||
            gapState.state.remark.status === 'approved') && (
            <ValidateText
              uuid={uuid}
              remark={gapState.state.remark}
              validationId={VALIDATION_ID}
              itemKey={itemKey}
              formStateKey={FORM_STATE_KEY}
              context={alertMessage}
            />
          )}

        <Alert
          status={gapState.getCompactAlertStatus()}
          mt="10px"
          fontSize="14px"
          mb="12px"
        >
          <Checkbox
            isChecked={gapState.state.noClaimsConfirmed}
            onChange={gapState.handlers.handleNoClaimsConfirmedChange}
            isDisabled={isBinderDisabled}
            id={fieldIds.noClaimsConfirmed}
          >
            I have confirmed with the customer there were no claims of any kind
            during the lapse
          </Checkbox>
        </Alert>

        {gapState.state.file && (
          <ValidateFile
            uuid={uuid}
            file={gapState.state.file}
            validationId={VALIDATION_ID}
            itemKey={itemKey}
            formStateKey={FORM_STATE_KEY}
          />
        )}

        {/* Market Approval (same as 006) */}
        <Text fontWeight="semibold" mt="20px" mb="10px">
          Market Approval
        </Text>

        {marketApprovalState.state.remark && (
          <VStack spacing={4} align="stretch" mb="15px">
            <Box id={fieldIds.remarks}>
              <SharedTextFieldInput
                label="Remarks"
                value={marketApprovalState.state.remark.text || ''}
                onChange={marketApprovalState.handlers.handleRemarksChange}
                borderColor={
                  !marketApprovalState.state.remark.text ||
                  marketApprovalState.state.remark.text.trim().length === 0
                    ? 'red'
                    : 'inherit'
                }
                formStateKey={FORM_STATE_KEY}
                section={section}
              />
            </Box>
            <ValidateText
              uuid={uuid}
              remark={marketApprovalState.state.remark}
              validationId={VALIDATION_ID}
              validationTypeKey="remarks_market_approval"
              itemKey={marketApprovalItemKey}
              formStateKey={FORM_STATE_KEY}
              context={[
                marketApprovalState.alertMessage,
                marketApprovalState.state.approvalContext,
              ]
                .filter(Boolean)
                .join(' ')}
            />
          </VStack>
        )}

        <FormControl mb="15px" id={fieldIds.approvalMethod}>
          <FormLabel fontWeight="semibold">Approval Method</FormLabel>
          <RadioGroup
            value={marketApprovalState.state.approvalMethod || ''}
            onChange={marketApprovalState.handlers.handleApprovalMethodChange}
            isDisabled={isBinderDisabled}
          >
            <Stack direction="row" spacing={5}>
              <Radio value="email">I got approval via email</Radio>
              <Radio value="phone_call">I got approval via phone call</Radio>
            </Stack>
          </RadioGroup>
        </FormControl>

        {marketApprovalState.state.approvalMethod === 'email' &&
          marketApprovalState.state.file && (
            <Box>
              <Text fontSize="sm" color="gray.600" mb="10px">
                {isMarketApprovalEmailEmlMsgEnabled
                  ? 'Upload market approval email as evidence (.eml or .msg only)'
                  : 'Upload market approval email as evidence'}
              </Text>
              <ValidateFile
                uuid={uuid}
                file={marketApprovalState.state.file}
                validationId={VALIDATION_ID}
                itemKey={marketApprovalItemKey}
                formStateKey={FORM_STATE_KEY}
              />
            </Box>
          )}

        {marketApprovalState.state.showPhoneCallFields &&
          marketApprovalState.state.remark && (
            <VStack spacing={4} align="stretch" mt="10px">
              <Box>
                <SharedTextFieldInput
                  label="Summary of Market Conversation"
                  value={
                    marketApprovalState.state.summaryOfMarketConversationText ||
                    ''
                  }
                  onChange={marketApprovalState.handlers.handleSummaryChange}
                  borderColor={
                    !marketApprovalState.state
                      .summaryOfMarketConversationText ||
                    marketApprovalState.state.summaryOfMarketConversationText.trim()
                      .length === 0
                      ? 'red'
                      : 'inherit'
                  }
                  formStateKey={FORM_STATE_KEY}
                  section={section}
                />
              </Box>

              <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                <Box flex={1} id={fieldIds.date}>
                  <DateField
                    label="Approval Date"
                    date={parseDateString(
                      marketApprovalState.state.approvalDate,
                    )}
                    onChange={marketApprovalState.handlers.handleDateChange}
                    required
                    isDisabled={isBinderDisabled}
                    htmlID={fieldIds.date}
                  />
                  {marketApprovalState.state
                    .approvalOutsideBindingAuthority && (
                    <Text fontSize="sm" color="red.500" mt="5px">
                      {BINDING_AUTHORITY_MESSAGE}
                    </Text>
                  )}
                </Box>
                <Box flex={1} id={fieldIds.time}>
                  <TimeField
                    label="Approval Time"
                    value={marketApprovalState.state.approvalTime || ''}
                    onChange={marketApprovalState.handlers.handleTimeChange}
                    required
                    isDisabled={isBinderDisabled}
                    htmlID={fieldIds.time}
                  />
                </Box>
              </Stack>

              <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                <FormControl isRequired flex={1} id={fieldIds.underwriter}>
                  <FormLabel>Underwriter Name</FormLabel>
                  <Input
                    variant="main"
                    value={marketApprovalState.state.underwriterName || ''}
                    onChange={
                      marketApprovalState.handlers.handleUnderwriterNameChange
                    }
                    disabled={isBinderDisabled}
                    borderColor={
                      !marketApprovalState.state.underwriterName ||
                      marketApprovalState.state.underwriterName.trim()
                        .length === 0
                        ? 'red'
                        : 'inherit'
                    }
                  />
                </FormControl>

                <FormControl isRequired flex={1} id={fieldIds.market}>
                  <FormLabel>Market</FormLabel>
                  <Select
                    variant="main"
                    value={marketApprovalState.state.market || ''}
                    onChange={(e) =>
                      marketApprovalState.handlers.handleMarketChange(
                        e.target.value,
                      )
                    }
                    disabled={isBinderDisabled}
                    borderColor={
                      !marketApprovalState.state.market ||
                      marketApprovalState.state.market.trim().length === 0
                        ? 'red'
                        : 'inherit'
                    }
                  >
                    <option value="">Select market...</option>
                    {carrierOptions.map((opt) => (
                      <option key={opt.value} value={opt.label}>
                        {opt.label}
                      </option>
                    ))}
                  </Select>
                  {marketApprovalState.state.marketMismatch && (
                    <Text fontSize="sm" color="red.500" mt="5px">
                      {MARKET_MISMATCH_MESSAGE}
                    </Text>
                  )}
                </FormControl>
              </Stack>

              <FormControl>
                <FormLabel>Approval Number (Optional)</FormLabel>
                <Input
                  variant="main"
                  value={marketApprovalState.state.approvalNumber || ''}
                  onChange={
                    marketApprovalState.handlers.handleApprovalNumberChange
                  }
                  disabled={isBinderDisabled}
                />
              </FormControl>
            </VStack>
          )}
      </Box>
    </Box>
  );
};

export default Validation090bResolver;
